import axiosInstance from '../../api/axios';
import { scopeParams, type AnalyticsScope } from './scope.ts';

// Row types mirror the backend row structs (and their `json` tags) verbatim —
// see skeptics-backend/repository/analytics_reports_repository.go (CAE
// section, reports #9-#19). Do not rename fields without updating both sides
// of the contract.

/** Report #9 — Control Universe Inventory. */
export type CAEControlUniverseRow = {
  control_id: string;
  title: string;
  control_type: string;
  frequency: string | null;
  status: string;
  significance: string | null;
  department: string | null;
  classification: string | null;
  mrc_control: boolean;
  external_auditor_reliance: boolean;
  itgc_domain: string | null;
};

/** Report #10 — Testing Status by Control (full version — assignee/due). */
export type CAETestingStatusRow = {
  control_id: string;
  title: string;
  workflow_status: string | null;
  assignee_user_id: number | null;
  assignee_name: string | null;
  due_date: string | null;
  overall_status: string | null;
  conclusion: string | null;
  attributes_passed: number | null;
  attributes_failed: number | null;
  testing_period_start: string | null;
  testing_period_end: string | null;
};

/** Report #11 — Issues Pipeline. */
export type CAEIssuesPipelineRow = {
  id: string;
  title: string;
  status: string;
  classification: string | null;
  department: string | null;
  total_actions: number;
  completed_actions: number;
  earliest_due: string | null;
};

/** Report #12 — Risk-to-Control Coverage Matrix. */
export type CAECoverageMatrixRow = {
  risk_id: string | null;
  risk_description: string | null;
  risk_level: string | null;
  mapped_controls: number;
};

/** Report #13 — MRC (Management Review Control) Dashboard. */
export type CAEMRCRow = {
  control_id: string;
  title: string;
  frequency: string | null;
  status: string;
  overall_status: string | null;
  conclusion: string | null;
};

/** Report #14 — Exceptions Summary. */
export type CAEExceptionsSummaryRow = {
  control_id: string;
  title: string;
  exceptions: number | null;
  avg_deviation: number | null;
  failed_runs: number;
};

/** Report #15 — External Auditor Reliance. */
export type CAEAuditorRelianceRow = {
  control_id: string;
  title: string;
  significance: string | null;
  status: string;
  overall_status: string | null;
};

/** Report #16 — Action Plan Aging. */
export type CAEActionPlanAgingRow = {
  id: string;
  issue_title: string;
  action_title: string;
  status: string;
  owner_name: string | null;
  due_date: string | null;
  days_overdue: number | null;
};

/** Report #17 — Controls by Department. */
export type CAEControlsByDeptRow = {
  dept: string;
  control_type: string;
  total: number;
  active: number;
  key_ctrl: number;
};

/** Report #18 — ITGC Domain Coverage. */
export type CAEITGCCoverageRow = {
  code: string;
  name: string;
  controls: number;
  active: number;
  passed: number;
};

/** Report #19 — Tester Workload Distribution (full version — assignee/due). */
export type CAETesterWorkloadRow = {
  user_id: number;
  tester: string;
  assigned: number;
  completed: number;
  next_due: string | null;
};

type RowsEnvelope<T> = { rows: T[] };

async function getRows<T>(path: string, scope?: AnalyticsScope): Promise<T[]> {
  const response = await axiosInstance.get<RowsEnvelope<T>>(path, { params: scopeParams(scope) });
  return response?.data?.rows ?? [];
}

const BASE = '/api/analytics/cae';

export const fetchCaeControlUniverse = (scope?: AnalyticsScope): Promise<CAEControlUniverseRow[]> =>
  getRows<CAEControlUniverseRow>(`${BASE}/control-universe`, scope);

export const fetchCaeTestingStatus = (scope?: AnalyticsScope): Promise<CAETestingStatusRow[]> =>
  getRows<CAETestingStatusRow>(`${BASE}/testing-status`, scope);

export const fetchCaeIssuesPipeline = (scope?: AnalyticsScope): Promise<CAEIssuesPipelineRow[]> =>
  getRows<CAEIssuesPipelineRow>(`${BASE}/issues-pipeline`, scope);

export const fetchCaeCoverageMatrix = (scope?: AnalyticsScope): Promise<CAECoverageMatrixRow[]> =>
  getRows<CAECoverageMatrixRow>(`${BASE}/coverage-matrix`, scope);

export const fetchCaeMRC = (scope?: AnalyticsScope): Promise<CAEMRCRow[]> => getRows<CAEMRCRow>(`${BASE}/mrc`, scope);

export const fetchCaeExceptionsSummary = (scope?: AnalyticsScope): Promise<CAEExceptionsSummaryRow[]> =>
  getRows<CAEExceptionsSummaryRow>(`${BASE}/exceptions-summary`, scope);

export const fetchCaeAuditorReliance = (scope?: AnalyticsScope): Promise<CAEAuditorRelianceRow[]> =>
  getRows<CAEAuditorRelianceRow>(`${BASE}/auditor-reliance`, scope);

export const fetchCaeActionPlanAging = (scope?: AnalyticsScope): Promise<CAEActionPlanAgingRow[]> =>
  getRows<CAEActionPlanAgingRow>(`${BASE}/action-plan-aging`, scope);

export const fetchCaeControlsByDept = (scope?: AnalyticsScope): Promise<CAEControlsByDeptRow[]> =>
  getRows<CAEControlsByDeptRow>(`${BASE}/controls-by-dept`, scope);

export const fetchCaeITGCCoverage = (scope?: AnalyticsScope): Promise<CAEITGCCoverageRow[]> =>
  getRows<CAEITGCCoverageRow>(`${BASE}/itgc-coverage`, scope);

export const fetchCaeTesterWorkload = (scope?: AnalyticsScope): Promise<CAETesterWorkloadRow[]> =>
  getRows<CAETesterWorkloadRow>(`${BASE}/tester-workload`, scope);
